/**
 * SideBar React component
 * 
 * @author    Jens Gruschel, Maurizio Tidei
 * @copyright © 2018 contexagon GmbH
 */


import React from 'react';
import {connect} from 'react-redux';
import {withStyles} from '@material-ui/core/styles';
import _ from 'lodash';

// material UI
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';

// application
import {filterObjects} from '../../operations/listing';
import {selectMenuItemAction} from '../../redux/menu/actions';
import {openPage} from '../../operations/view';
// import {ANALYTICS_PAGE} from '../redux/view/consts';


const getItemStyle = (level) => ({
    paddingLeft: level * 20 + 16
});


/**
 * SideBar contains a menu for filters and possibly other actions
 */
export class SideBar extends React.Component {

    render() {

        return (
            <div style={{position: 'sticky', top: 64, zIndex: 99}}>
                <MenuList>
                    {this.renderItems(this.props.mainMenu, [])}
                </MenuList>
            </div>
        );
    }

    renderItems(menu, path) {

        const menuFields = menu.fields || {};
        const menuItemRefs = menuFields.items || [];

        return _.flatMap(menuItemRefs, ref => {
            const item = this.props.menuItems[ref];
            if (item) {
                return this.renderItem(item, path);
            }
            else {
                return [];
            }
        });
    }

    renderItem(item, path) {

        const selected = this.props.selectedItem === item;
        const level = path.length;
        const opened = this.props.openedItems[level] === item;

        if (item.type === "menu") {
            return this.renderMenu(item, path, selected, opened);
        }
        else if (item.type === "filter") {
            return this.renderFilter(item, path, selected, opened);
        }
        else if (item.type === "custompage") {
            return this.renderCustomPage(item, path, selected, opened);
        }
        else {
            return [];
        }
    }

    renderMenu(menu, path, selected, opened) {

        const level = path.length;
        const {classes} = this.props;

        let result = [<MenuItem
            key={menu.id}
            className={classes.menuItem}
            onClick={event =>
                this.props.selectMenuItemAction(menu, [...path, menu])
            }
            selected={selected}
            style={getItemStyle(level)}
        >
            <ListItemIcon className={classes.icon}>
                <img className={classes.iconImage} src={menu.fields.iconurl} alt=" " width={24} height={24}/>
            </ListItemIcon>
            <ListItemText className={classes.menuText} inset primary={menu.fields.name} />
        </MenuItem>];

        // add sub items
        const items = this.renderItems(menu, [...path, menu]);
        if (items.length > 0) {
            result.push(
                <Collapse key={"children-" + menu.id} in={opened}>
                    <MenuList className={classes.subMenuList}>
                        {items}
                    </MenuList>
                </Collapse>
            );
        }

        return result;
    }

    renderFilter(filter, path, selected, opened) {

        const level = path.length;
        const {classes} = this.props;

        let result = [<MenuItem
            key={filter.id}
            className={classes.filterItem}
            onClick={event => {
                    this.props.selectMenuItemAction(filter, [...path, filter]);
                    this.props.filterObjects(filter);
                }
            }
            selected={selected}
            style={getItemStyle(level)}
        >
            <ListItemIcon className={classes.icon}>
                <img className={classes.iconImage} src={filter.fields.iconurl} alt=" " width={24} height={24}/>
            </ListItemIcon>
            <ListItemText classes={{root: classes.filterText, primary: classes.filterPrimary}} inset primary={filter.fields.name} />
        </MenuItem>];

        // add sub items for categories in case there are filter groups
        const subFilters = this.props.subFilters[filter.id];
        if (subFilters && subFilters.length > 0) {
            const items = subFilters.map(subFilter => this.renderSubFilter(subFilter, [...path, filter]));
            result.push(
                <Collapse key={"children-" + filter.id} in={opened}>
                    <MenuList dense className={classes.subMenuList}>
                        {items}
                    </MenuList>
                </Collapse>
            );
        }

        return result;
    }
    
    renderSubFilter(subFilter, path) {

        const level = path.length;
        const {classes} = this.props;

        return <MenuItem
            key={subFilter.id}
            className={classes.subFilterItem}
            onClick={event => {
                    this.props.selectMenuItemAction(subFilter, [...path, subFilter]);
                    this.props.filterObjects(subFilter);
                }
            }
            selected={this.props.selectedItem === subFilter}
            style={getItemStyle(level - 1)}
        >
            <ListItemText classes={{root: classes.subFilterText, primary: classes.subFilterPrimary}} inset primary={subFilter.fields.name} />
        </MenuItem>;
    }

    renderCustomPage(customPage, path, selected, opened) {

        const level = path.length;
        const {classes} = this.props;

        return [<MenuItem
            key={customPage.id}
            className={classes.filterItem}
            onClick={event => {
                    this.props.selectMenuItemAction(customPage, [...path, customPage]);
                    this.props.openPage(customPage.fields.id);
                }
            }
            selected={selected}
            style={getItemStyle(level)}
        >
            <ListItemIcon className={classes.icon}>
                <img className={classes.iconImage} src={customPage.fields.iconurl} alt=" " width={24} height={24}/>
            </ListItemIcon>
            <ListItemText classes={{root: classes.filterText, primary: classes.filterPrimary}} inset primary={customPage.fields.name} />
        </MenuItem>];
    }
}

const styles = theme => ({
    subMenuList: {
        paddingBottom: 12,
        paddingTop: 0
    },
    menuItem: {
    },
    filterItem: {
        '&:focus': {
            backgroundColor: theme.palette.primary.main,
            '& span': {
                color: theme.palette.common.white
            },
        }
    },
    subFilterItem: {
        '& span': {
            marginLeft: -36
        },
        '&:focus': {
            backgroundColor: theme.palette.primary.main,
            '& span': {
                color: theme.palette.common.white
            },
        }
    },
    menuText: {
        paddingLeft: 8,
        '& span': {
            textTransform: "uppercase"
        }
    },
    filterText: {
        paddingLeft: 8,
        '& span': {
            opacity: 0.8
        }
    },
    subFilterText: {
        paddingLeft: 8,
        '& span': {
            opacity: 0.8
        }
    },
    icon: {},
    iconImage: {
        opacity: 0.53
    }
});

const mapStateToProps = state => {
    return {
        menuItems: state.menu.items,
        mainMenu: state.menu.mainMenu,
        selectedItem: state.menu.selectedItem,
        openedItems: state.menu.openedItems,
        subFilters: state.menu.subFilters,
        currentFilter: state.menu.currentFilter
    }
};

export default connect(mapStateToProps, {selectMenuItemAction, filterObjects, openPage})(withStyles(styles)(SideBar));
